import {
  CircularProgress,
  Typography,
  Grid,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import React, { Fragment, useEffect, useState } from "react";
import {
  CreateProps,
  useRedirect,
  useNotify,
  useMutation,
  Toolbar,
  Button,
  Create,
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  required,
  TextInput,
  maxLength,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  FormDataConsumer,
  number,
  regex,
} from "react-admin";
import { centToDollar, GetParam } from "utils/tools";
import SaveIcon from "@material-ui/icons/Save";
import { useFormState } from "react-final-form";
import InvoiceInformation from "./Component/InvoiceInformation";
import { ableCreateManualBankTransferValidation } from "utils/validation";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
  },
});

const CashReceiptsCreate = (props: CreateProps) => {
  const [sumInvoicesAmount, setSumInvoicesAmount] = useState<number>(0);

  const param = GetParam();
  const classes = useStyles();

  const personType = param.get("personType");
  const accountingCustomerId = Number(param.get("accountingCustomerId"));

  const title =
    personType === "personInCharges" ? "Person In Charge" : "Parents";

  const breadcrumb = [
    {
      title: title,
      link: `/${personType}/${param.get("parentId")}/show`,
    },
    {
      title: "Financial",
      link: `/${personType}/${param.get("parentId")}/show/1`,
    },
    {
      title: `Create Bank Transfer`,
      link: "",
    },
  ];

  const BankTransferToolbar = (props: any) => {
    const [open, setOpen] = useState<boolean>(false);

    const { values } = useFormState();

    const redirect = useRedirect();
    const notify = useNotify();

    const handleOpenDialogIssueCn = () => {
      setOpen(true);
    };
    const handleCloseDialogIssueCn = () => {
      setOpen(false);
    };

    const paymentIsMoreThanTotalInvoices = (
      invoiceAmount: number,
      payAmount: number
    ) => {
      const totalAmount = payAmount - invoiceAmount;
      return totalAmount > 0;
    };

    const [handleSaveBankTransfer, { loading }] = useMutation(
      {
        type: "create",
        resource: "cashReceipts",
        payload: {
          data: {
            accountingDocumentIds: values.accountingDocumentIds,
            parentId: values.parentId,
            amount: Number(Math.round(values.price * 100).toFixed(2)),
            remarks: values.description,
          },
        },
      },
      {
        onSuccess: ({ data }) => {
          notify(`Successfully save.`, "info");
          personType === "personInCharges"
            ? redirect(`/personInCharges/${props.record.parentId}/show/1`)
            : redirect(`/parents/${props.record.parentId}/show/1`);
        },
        onFailure: (error) =>
          notify(`Failed save, error: ${error.message}`, "warning"),
      }
    );

    return (
      <Toolbar {...props}>
        <Button
          onClick={() =>
            paymentIsMoreThanTotalInvoices(
              sumInvoicesAmount,
              Number(Math.round(values.price * 100).toFixed(2))
            )
              ? handleOpenDialogIssueCn()
              : handleSaveBankTransfer()
          }
          disabled={
            !ableCreateManualBankTransferValidation(values) || props.invalid
          }
          label={loading ? "SAVING..." : "SAVE"}
        >
          {loading ? (
            <CircularProgress size={18} thickness={2} />
          ) : (
            <SaveIcon />
          )}
        </Button>

        <Fragment>
          <Dialog
            open={open}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className={classes.dialog}
            maxWidth="md"
          >
            <DialogTitle id="alert-dialog-title">
              Issue Credit Note Confirmation
            </DialogTitle>
            <DialogContent>
              <DialogContentText color="initial">
                This Bank Transfer will issue a credit note with amount $
                {centToDollar(
                  Number(Math.round(values.price * 100).toFixed(2)) -
                    sumInvoicesAmount
                )}
                , Are you sure to create this payment ?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseDialogIssueCn}
                color="secondary"
                label="Back"
              />
              <Button
                onClick={handleSaveBankTransfer}
                color="primary"
                label={"Confirm"}
                autoFocus
              >
                {loading ? (
                  <CircularProgress size={18} thickness={2} />
                ) : (
                  <SaveIcon />
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </Fragment>
      </Toolbar>
    );
  };

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <Typography variant="h4">New Bank Transfer</Typography>
      <Create title={"Create Bank Transfer"} {...props}>
        <SimpleForm toolbar={<BankTransferToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">General Info</Typography>
            </Grid>

            <Grid item xs={8}>
              <ReferenceInput
                label={title}
                source="parentId"
                reference={personType}
                disabled
              >
                <AutocompleteInput
                  optionText={(choice: any) =>
                    `${choice.firstName} ${choice.lastName}`
                  }
                />
              </ReferenceInput>
            </Grid>

            <Grid item xs={8}>
              <ReferenceArrayInput
                source="accountingDocumentIds"
                filter={{
                  CustomerId: accountingCustomerId,
                  Status: "Posted",
                  IsOpen: true,
                }}
                reference="invoices"
              >
                <AutocompleteArrayInput
                  optionText={(choice) =>
                    `${choice.number} - ${
                      choice.items[0].description
                    } - $${centToDollar(choice.amountDue)}`
                  }
                />
              </ReferenceArrayInput>
            </Grid>

            <FormDataConsumer>
              {({ formData, ...rest }) => {
                return (
                  <Grid item xs={8}>
                    {formData.accountingDocumentIds &&
                      formData.accountingDocumentIds.length > 0 && (
                        <InvoiceInformation
                          setSumInvoicesAmount={setSumInvoicesAmount}
                        />
                      )}
                  </Grid>
                );
              }}
            </FormDataConsumer>

            <Grid item xs={12}>
              <Typography variant="h6">Bank Transfer Detail</Typography>
            </Grid>

            <Grid item xs={8}>
              <TextInput
                label="Price"
                source="price"
                validate={[number(), regex(/^\d+(?:\.\d{1,2})?$/), required()]}
              />
            </Grid>

            <Grid item xs={8}>
              <TextInput
                multiline={true}
                label="Description"
                source="description"
                inputProps={{ maxLength: 90 }}
                placeholder="Description is only up to 90 characters"
                validate={[required(), maxLength(90)]}
              />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </Create>
    </>
  );
};

export default CashReceiptsCreate;
